import Link from "next/link"
import React, { useMemo } from "react"
import styles from "@css/modules/Resorts.module.scss"
import { ArrowLeft } from "@css/icons/SVG"
import Image from "next/image"
import cn from "classnames"

/**
 * Generates a list of resorts to be displayed based on the search input, and handles the display of each resort item.
 *
 * @param {object} search - the search input
 * @param {array} resorts - the array of resorts to be displayed
 * @param {function} onFastSearch - the function to handle fast search
 * @param {boolean} [mobile] - a flag indicating if the display is on a mobile device
 * @return {JSX.Element} the JSX element containing the rendered list of resort items
 */
export default function ResortList({ search, resorts = [], onFastSearch, mobile }) {
    const resortsToShow = useMemo(
        () =>
            resorts.map((resort) => {
                resort.hidden = !resort.short_name.toLowerCase().includes(search.toLowerCase())
                return resort
            }),
        [search, resorts]
    )
    const renderedItems = useMemo(() => {
        return resortsToShow.map((res) => {
            const format = res?.logo?.formats?.small || res?.logo
            const className = cn(styles.cardMobile, res.hidden && styles.cardHidden)
            if (mobile) {
                return (
                    <div key={res.id} className={className}>
                        <Link href={`/resorts/${res.slug}`} className={styles.cardLink}>
                            <div className={styles.cardMobileContent}>
                                <Image alt={res.short_name} src={format?.url} className={styles.cardImage} width={format?.width} height={format?.height} />
                                <div>
                                    <h3 className={styles.cardName}>{res.short_name}</h3>
                                    <p className={styles.cardDescription}>{res.short_description}</p>
                                </div>
                                <ArrowLeft className={styles.arrowIcon} />
                            </div>
                        </Link>
                    </div>
                )
            }
            const classNameDesktop = cn(styles.card, res.hidden && styles.cardHidden)
            return (
                <div key={res.id} className={classNameDesktop}>
                    <Link href={`/resorts/${res.slug}`}>
                        <Image alt={res.short_name} src={format?.url} className={styles.cardImage} width={format?.width} height={format?.height} />
                        <h3 className={styles.cardName}>{res.short_name}</h3>
                        <div className={styles.cardCover}>
                            <div>
                                <h3 className={styles.coverCardName}>{res.short_name}</h3>
                                <p className={styles.coverCardDescription}>{res.short_description}</p>
                            </div>
                            <ArrowLeft className={styles.arrowIcon} />
                        </div>
                    </Link>
                </div>
            )
        })
    }, [resortsToShow])

    if (!resortsToShow.filter((i) => !i.hidden).length) {
        const fastSearch = (event) => onFastSearch(event.target.innerText)
        return (
            <div className={styles.emptyResult}>
                No search results found for <b>“{search}”</b>
                <p className={styles.emptyResultSub}>Try searching for other keywords </p>
                <ul className={styles.emptyResultList}>
                    <li onClick={fastSearch}>Polynesian</li>
                    <li onClick={fastSearch}>Boulder Ridge</li>
                    <li onClick={fastSearch}>Animal Kingdom</li>
                </ul>
            </div>
        )
    }

    return <div className={styles.cardWrapper}>{renderedItems}</div>
}
